"use client"

import type { ReactNode } from "react"
import {
  CircleAlertIcon,
  CircleCheckIcon,
  InfoIcon,
  TriangleAlertIcon,
} from "lucide-react"
import { toast, type ExternalToast } from "sonner"
import { cn } from "@/lib/utils"

type TaskToastVariant = "success" | "info" | "warning" | "error"

type VariantConfig = {
  className: string
  descriptionClassName: string
  icon: ReactNode
}

const variantConfig = {
  success: {
    className: "border-emerald-300 bg-emerald-50 text-emerald-950",
    descriptionClassName: "text-emerald-800",
    icon: <CircleCheckIcon className="size-4 text-emerald-600" aria-hidden="true" />,
  },
  info: {
    className: "border-blue-300 bg-blue-50 text-blue-950",
    descriptionClassName: "text-blue-800",
    icon: <InfoIcon className="size-4 text-blue-600" aria-hidden="true" />,
  },
  warning: {
    className: "border-amber-300 bg-amber-50 text-amber-950",
    descriptionClassName: "text-amber-800",
    icon: <TriangleAlertIcon className="size-4 text-amber-600" aria-hidden="true" />,
  },
  error: {
    className: "border-red-300 bg-red-50 text-red-950",
    descriptionClassName: "text-red-800",
    icon: <CircleAlertIcon className="size-4 text-red-600" aria-hidden="true" />,
  },
} satisfies Record<TaskToastVariant, VariantConfig>

function withTaskToastVariant(variant: TaskToastVariant, options: ExternalToast = {}): ExternalToast {
  const config = variantConfig[variant]

  return {
    ...options,
    className: cn(config.className, options.className),
    descriptionClassName: cn(config.descriptionClassName, options.descriptionClassName),
    icon: options.icon ?? config.icon,
  }
}

export const taskToast = {
  success(title: ReactNode, options?: ExternalToast) {
    return toast.success(title, withTaskToastVariant("success", options))
  },
  info(title: ReactNode, options?: ExternalToast) {
    return toast.info(title, withTaskToastVariant("info", options))
  },
  warning(title: ReactNode, options?: ExternalToast) {
    return toast.warning(title, withTaskToastVariant("warning", options))
  },
  error(title: ReactNode, options?: ExternalToast) {
    return toast.error(title, withTaskToastVariant("error", options))
  },
}
